import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Plus, Trash2, MessageSquare } from "lucide-react";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { BrokerChat, ScannerPanel } from "@/components/BrokerChat";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";

export default function Broker() {
  const [params, setParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(params.get("session"));
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/broker/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) setParams({ session: active }, { replace: true }); }, [active, setParams]);

  const newSession = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/broker/sessions", { tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setActive(data.session.session_id);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/broker/sessions/${id}`); if (active === id) setActive(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Bot className="w-7 h-7 text-emerald-500" />
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">AI Broker</h1>
            <p className="text-slate-400 text-sm">Your 24/7 personal broker — live data, exact timing, any language.</p>
          </div>
        </div>
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <Button onClick={newSession} disabled={creating} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="broker-new-session">
              <Plus className="w-4 h-4 mr-2" /> New conversation
            </Button>
            <div className="glass rounded-2xl p-3 max-h-[420px] overflow-y-auto" data-testid="broker-session-list">
              {sessions.length === 0 && <p className="text-xs text-slate-500 p-2">No conversations yet.</p>}
              {sessions.map((s) => (
                <div key={s.session_id} className={`group flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer text-sm ${active === s.session_id ? "bg-emerald-500/15 text-emerald-300" : "hover:bg-white/5 text-slate-300"}`}
                  onClick={() => setActive(s.session_id)} data-testid={`broker-session-${s.session_id}`}>
                  <MessageSquare className="w-4 h-4 shrink-0 opacity-60" />
                  <span className="flex-1 truncate">{s.title}</span>
                  <button onClick={(e) => { e.stopPropagation(); remove(s.session_id); }} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400" aria-label="Delete" data-testid={`broker-session-delete-${s.session_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <ScannerPanel />
          </div>
          <div className="lg:col-span-9 space-y-4">
            <div className="glass rounded-2xl p-5 h-[70vh] min-h-[520px] flex flex-col">
              {active ? (
                <BrokerChat key={active} sessionId={active} onSessionUpdate={load} className="h-full" />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-500 text-sm gap-3" data-testid="broker-empty">
                  <Bot className="w-10 h-10 opacity-40" />
                  <p>Start a new conversation, or analyze a chart in the AI Copilot — your broker opens automatically with the full breakdown.</p>
                  <Button onClick={newSession} disabled={creating} variant="outline" className="border-white/15 text-slate-300" data-testid="broker-empty-new">Start chatting</Button>
                </div>
              )}
            </div>
            <Disclaimer compact />
          </div>
        </div>
      </div>
    </Layout>
  );
}
